<template>
  <div class="modal overflow-auto" select-cy="update-supporter-details">
    <div class="card inner">
      <div class="header">
        <div class="title">
          <h2>{{ this.canEdit ? 'Edit' : 'View' }} Address</h2>
        </div>
        <div class="close" @click="close()">
          <i class="icn icn-x sm"></i>
        </div>
      </div>
      <div class="content">
        <form id="address-edit" @submit.prevent @submit="edit">
          <h3 class="sub-title">Details</h3>
          <div class="row mt-2" v-if="labels">
            <div class="field">
              <select :disabled="!canEdit" required v-model="editItem.label">
                <option :value="l" :selected="l === editItem.label" v-for="l of labels" :key="l">
                  {{ l }}
                </option>
              </select>
            </div>
          </div>
          <div class="row mt-2">
            <country-selector
              :disabled="!canEdit"
              :country="editItem.country"
              @countrySelected="clearAddress($event)"
            ></country-selector>
          </div>

          <div class="row mt-2" v-if="(editItem.country === australia) & canEdit">
            <autocomplete-address
              ref="autocomplete-address"
              @addressSelected="getSupporterSelectedAddress"
            ></autocomplete-address>
          </div>

          <div class="row mt-2">
            <div class="field">
              <input
                :disabled="!canEdit"
                v-model="editItem.unitNumber"
                type="text"
                placeholder="Unit number"
                autocomplete="nope"
                maxlength="31"
              />
            </div>

            <div class="field">
              <input
                :disabled="!canEdit"
                required
                v-model="editItem.streetNumber"
                type="text"
                placeholder="Street number"
                autocomplete="nope"
                maxlength="31"
              />
            </div>
          </div>

          <div class="row mt-2">
            <div class="field">
              <input
                :disabled="!canEdit"
                required
                v-model="editItem.streetName"
                type="text"
                placeholder="Street name"
                autocomplete="nope"
                maxlength="127"
              />
            </div>

            <div class="field">
              <input
                :disabled="!canEdit"
                required
                v-model="editItem.suburb"
                type="text"
                placeholder="Suburb"
                autocomplete="nope"
                maxlength="63"
              />
            </div>
          </div>

          <div class="row mt-2">
            <div class="field">
              <input
                :disabled="!canEdit"
                required
                v-model="editItem.postalCode"
                type="text"
                placeholder="Postcode"
                autocomplete="nope"
                maxlength="15"
              />
            </div>
            <div class="field">
              <select
                :disabled="!canEdit"
                required
                name="state"
                v-if="editItem.country === australia"
                v-model="editItem.state"
              >
                <option value disabled selected>Select a state</option>
                <option value="NSW">NSW</option>
                <option value="ACT">ACT</option>
                <option value="NT">NT</option>
                <option value="SA">SA</option>
                <option value="WA">WA</option>
                <option value="VIC">VIC</option>
                <option value="QLD">QLD</option>
                <option value="TAS">TAS</option>
              </select>
              <input
                v-if="editItem.country !== australia"
                v-model="editItem.state"
                type="text"
                placeholder="State"
                autocomplete="nope"
                maxlength="63"
              />
            </div>
          </div>
          <!-- STATUS -->
          <h3 class="sub-title">Status</h3>
          <div class="row mt-2">
            <div class="field">
              <Toggle ref="togglePrimary" label="Primary" :default="editItem.isPrimary" :disabled="!canEdit"></Toggle>
            </div>
            <div class="field">
              <Toggle
                ref="toggleDoNotMail"
                label="Do Not Mail"
                :default="editItem.doNotMail"
                :disabled="!canEdit"
              ></Toggle>
            </div>
          </div>
        </form>
      </div>

      <!-- Error Messages -->
      <Alert v-if="errorMessage" :message="errorMessage"></Alert>

      <div class="footer" v-if="canEdit">
        <button class="secondary" type="button" @click="close()">Cancel</button>
        <div class="flex-grow"></div>
        <button type="button" @click="edit()" :disabled="!canSubmit" select-cy="submit-add-supporter">
          <span v-if="!isValidating">Save</span>
          <span class="icon" v-if="isValidating">
            <i class="icn icn-refresh sm animate-reverse-spin"></i>
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ALERT_TYPE, MESSAGES, ADDRESS_LABEL, CONTACT_TYPE } from '@/helpers/constants';
import { MODAL_NAME } from '../../stores/supporters';
import AutocompleteAddress from '@/components/AutocompleteAddress.vue';
import CountrySelector from '@/components/CountrySelector.vue';
import Alert from '@/components/Alert.vue';
import Toggle from '@/components/Toggle';

export default {
  name: 'SupporterEditAddress',
  components: {
    Alert,
    AutocompleteAddress,
    CountrySelector,
    Toggle,
  },

  data() {
    return {
      australia: 'Australia',
      editItem: {
        id: null,
        addressLines: null,
        label: null,
        country: null,
        streetNumber: null,
        streetName: null,
        suburb: null,
        postalCode: null,
        state: null,
        unitNumber: null,
        doNotMail: undefined,
        isPrimary: undefined,
      },
      isValidating: false,
      isProcessing: false,
      message: null,
    };
  },
  computed: {
    canSubmit() {
      return !this.isProcessing;
    },
    labels() {
      if (this.editItem) {
        return Object.values(ADDRESS_LABEL['ADDRESS']);
      }
      return null;
    },
    errorMessage: {
      get() {
        return this.message;
      },
      set(value) {
        if (value === null) {
          this.message = null;
        } else {
          this.message = {
            content: value,
            type: ALERT_TYPE.ERROR,
          };
        }
      },
    },
  },

  created() {
    this.editItem = { ...this.address };
  },

  props: {
    address: Object,
    supporter: Object,
    canEdit: Boolean,
  },

  methods: {
    close() {
      this.$store.commit('supporters/close', MODAL_NAME.EDIT_ADDRESS);
    },
    async edit() {
      if (this.isValidating) return;
      this.errorMessage = null;
      this.isValidating = true;
      this.editItem.addressLines = `${this.editItem.unitNumber ? this.editItem.unitNumber + ', ' : ''} ${
        this.editItem.streetNumber
      } ${this.editItem.streetName}`;
      const isValid = document.querySelector('#address-edit').reportValidity();
      if (isValid) {
        this.editItem.isPrimary = this.$refs.togglePrimary.get();
        this.editItem.doNotMail = this.$refs.toggleDoNotMail.get();

        if (this.isChanged()) {
          const payload = {
            supporterId: this.supporter.id,
            addresses: [this.editItem],
          };
          console.log('payload', payload);

          // Create update details for WWF people to update to RE
          if (this.supporter.reConstituentId) {
            this.createUpdateDetails(payload);
          }

          // Send to Pandata
          this.$store
            .dispatch('supporters/createUpdateAddress', [payload])
            .then(() => {
              this.$store.commit('supporters/refresh');
              this.close();
            })
            .catch((err) => {
              console.error(err);
              if (err && err.status && err.status === 401) {
                this.errorMessage = MESSAGES.NO_PERMISSION;
              } else {
                this.errorMessage = MESSAGES.SERVER_ERROR;
              }
            });
        } else {
          this.close();
        }
      }
      this.isValidating = false;
    },
    isChanged() {
      return (
        this.address.label !== this.editItem.label ||
        this.address.country !== this.editItem.country ||
        this.address.addressLines !== this.editItem.addressLines ||
        this.address.suburb !== this.editItem.suburb ||
        this.address.postalCode !== this.editItem.postalCode ||
        this.address.state !== this.editItem.state ||
        this.address.isPrimary !== this.editItem.isPrimary ||
        this.address.doNotMail !== this.editItem.doNotMail
      );
    },
    removeValidity() {
      document.querySelector('#address').setCustomValidity('');
    },
    getSupporterSelectedAddress({ address, fullLine }) {
      this.editItem = { ...this.editItem, ...address };
      this.editItem.unitNumber =
        address.unitNumber && address.unitNumber.trim() !== '' ? address.unitNumber.trim() : null;
      this.editItem.addressFullLine = `${fullLine}, ${this.editItem.country}`;
    },
    clearAddress(event) {
      this.editItem.country = event.country;
      this.editItem.addressFullLine = null;
      this.editItem.streetNumber = null;
      this.editItem.streetName = null;
      this.editItem.suburb = null;
      this.editItem.postalCode = null;
      this.editItem.state = null;
      this.editItem.unitNumber = null;
    },
    createUpdateDetails(data) {
      const payload = {
        ...data,
        email: this.supporter.contacts
          .filter((contact) => contact.type === CONTACT_TYPE.EMAIL)
          .sort((a, b) => (b.isPrimary ? 1 : -1))[0]?.contact,
      };
      payload.reConstituentId = this.supporter.reConstituentId;
      payload.createdAt = new Date();

      this.$store
        .dispatch('supporters/createUpdateDetails', [payload])
        .then(() => {
          this.$store.dispatch('notifications/success', {
            headline: 'Contact Details Updated',
            details: 'The request has been submitted.',
          });
          this.close();
        })
        .catch((err) => {
          console.error(err);
          if (err && err.status && err.status === 401) {
            this.errorMessage = MESSAGES.NO_PERMISSION;
          } else {
            this.errorMessage = MESSAGES.SERVER_ERROR;
          }
        })
        .finally(() => (this.isValidating = false));
    },
  },
};
</script>
